import type {
  ResourceApplyResult,
  ResourceDefinition,
  ResourceInput,
  ResourceInspection,
  ResourceOwnership,
  ResourceValue,
} from "./types";

/** The fields every inspection takes from its resource definition. */
export const inspectionIdentity = (definition: ResourceDefinition) => ({
  definition,
  key: definition.key,
  kind: definition.kind,
  label: definition.label,
  stepId: definition.stepId,
  applicable: definition.applicable,
});

/** Owned when the adapter's resource was found; a conflict when an unmanaged one is in the way. */
export const resourceOwnership = (owned: boolean, conflict: boolean): ResourceOwnership =>
  owned ? "owned" : conflict ? "conflict" : "missing";

/** "<description> already exists but is not managed by this integration." */
export const unmanagedResourceMessage = (description: string): string =>
  `${description} already exists but is not managed by this integration.`;

/** Thrown by `ensure` when an unmanaged resource with the adapter's name is in the way. */
export const unmanagedResourceError = (description: string): Error => new Error(unmanagedResourceMessage(description));

/** The `ensure` result for a resource the current answers don't need. */
export const notRequiredResult = (definition: ResourceDefinition): ResourceApplyResult => ({
  message: `${definition.label} is not required for this setup.`,
});

type InspectionState = "conflict" | "missing" | "incomplete" | "ready" | "error";

/**
 * The inspection of a resource the adapter creates and then owns (a mapping, connection or connector):
 * identity from its definition, and ownership and state from whether it was found, whether an unmanaged
 * resource with the same name is in the way, and whether it's complete.
 */
export const inspectOwnedResource = <TValue extends ResourceValue>(
  definition: ResourceDefinition,
  {
    found,
    conflict,
    inputs,
    complete = Boolean(found) && inputs.every((input) => input.complete),
    error = Boolean(found) && inputs.some((input) => input.error),
    messages,
    ...details
  }: {
    found?: TValue & { id?: string };
    /** An existing resource with the same name that the adapter doesn't manage. */
    conflict?: unknown;
    inputs: ResourceInput[];
    /** Defaults to found with every input complete. */
    complete?: boolean;
    /** Found but set up in a way that breaks it; defaults to any input being in error. */
    error?: boolean;
    /** The conflict and error messages are only needed when those states can happen (error falls back to incomplete). */
    messages: Record<Exclude<InspectionState, "conflict" | "error">, string> & { conflict?: string; error?: string };
  } & Pick<ResourceInspection<TValue>, "href" | "linkLabel" | "action" | "dependsOn">,
): ResourceInspection<TValue> => {
  const state: InspectionState = conflict ? "conflict" : !found ? "missing" : complete ? "ready" : error ? "error" : "incomplete";
  return {
    ...inspectionIdentity(definition),
    ownership: resourceOwnership(Boolean(found), Boolean(conflict)),
    exists: Boolean(found),
    complete,
    state,
    message: (state === "error" ? messages.error ?? messages.incomplete : messages[state]) ?? "",
    resourceId: found?.id,
    value: found,
    inputs,
    completeCount: inputs.filter((input) => input.complete).length,
    ...details,
  };
};
